import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  Stack,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CelebrationIcon from '@mui/icons-material/Celebration';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate, utcMs } from '../utils/dateFormat';
import { downloadWorkforceInsightsExcel } from '../utils/workforceInsightsExport';
import OrganisationWorkforceHelpSidebar from '../components/OrganisationWorkforceHelpSidebar';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import type { OrganogramView } from '../types';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

const STAT_CARD_PURPLE: SxProps<Theme> = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
  height: '100%',
};

const STAT_CARD_GREEN: SxProps<Theme> = {
  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  color: 'white',
  boxShadow: '0 8px 24px rgba(67, 233, 123, 0.3)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(67, 233, 123, 0.4)' },
  height: '100%',
};

const STAT_CARD_ORANGE: SxProps<Theme> = {
  background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
  color: 'white',
  boxShadow: '0 8px 24px rgba(255, 167, 38, 0.3)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 167, 38, 0.4)' },
  height: '100%',
};

/** Header cells: labels + sort icons stay on one line. */
const WORKFORCE_TABLE_HEAD_CELL_SX: SxProps<Theme> = { whiteSpace: 'nowrap' };

/** Body cells where long text should truncate with an ellipsis (aligned with Dashboard / Email logs patterns). */
const WORKFORCE_BODY_ELLIPSIS_CELL_SX: SxProps<Theme> = {
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

interface WorkforceGradientStatCardProps {
  sx: SxProps<Theme>;
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
}

function WorkforceGradientStatCard({ sx, label, value, icon, onClick }: WorkforceGradientStatCardProps) {
  return (
    <Card
      sx={{
        ...sx,
        ...(onClick
          ? {
              cursor: 'pointer',
              '&:focus-visible': {
                outline: '2px solid rgba(255,255,255,0.9)',
                outlineOffset: '-2px',
              },
            }
          : {}),
      }}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
        <Box>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="h3" component="div" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        {icon ? (
          <Box sx={{ alignSelf: 'flex-end', mt: 1, opacity: 0.85, display: 'flex' }}>{icon}</Box>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface TenureDistribution {
  underOneYear: number;
  oneToThreeYears: number;
  threeToFiveYears: number;
  fiveToTenYears: number;
  tenYearsPlus: number;
}

interface CelebrationItem {
  userId: string;
  fullName: string;
  primaryDepartmentName?: string | null;
  occurrenceLocalDate: string;
  milestoneYears?: number | null;
}

interface CalendarYearAttrition {
  year: number;
  leavers: number;
  openingHeadcount: number;
  closingHeadcount: number;
  averageHeadcount: number;
  attritionRatePercent?: number | null;
  isYearToDate: boolean;
}

interface LeaverItem {
  userId: string;
  fullName: string;
  primaryDepartmentName?: string | null;
  dateOfExit: string;
  dateOfEngagement: string;
  tenureAtExitDisplay?: string | null;
}

interface EmployeeRow {
  userId: string;
  fullName: string;
  email?: string | null;
  businessUnitName?: string | null;
  departmentsDisplay: string;
  dateOfBirth?: string | null;
  dateOfEngagement?: string | null;
  tenureYearsApprox?: number | null;
  tenureDisplay?: string | null;
  nextWorkAnniversaryLocal?: string | null;
  missingCompanyForLeave?: boolean;
  missingRoleTemplate?: boolean;
  missingDateOfExit?: boolean;
  isActive?: boolean;
}

interface WorkforceInsightsResponse {
  organisationTimeZoneIana: string;
  asOfDateLocal: string;
  headcount: number;
  medianYearsTenure?: number | null;
  meanYearsTenure?: number | null;
  companyMeanYearsTenure?: number | null;
  companyMedianYearsTenure?: number | null;
  companyHeadcount?: number | null;
  tenureDistribution: TenureDistribution;
  birthdaysThisMonthCount: number;
  workAnniversariesThisMonthCount: number;
  newJoinersLast90DaysCount: number;
  milestoneAnniversariesThisQuarterCount: number;
  missingDateOfBirthCount: number;
  missingDateOfEngagementCount: number;
  missingDateOfExitCount: number;
  missingCompanyForLeaveCount: number;
  missingRoleTemplateCount: number;
  birthdaysThisMonth: CelebrationItem[];
  workAnniversariesThisMonth: CelebrationItem[];
  newJoinersLast90Days: CelebrationItem[];
  milestoneAnniversariesThisQuarter: CelebrationItem[];
  calendarYearAttrition: CalendarYearAttrition[];
  leaversThisCalendarYear: LeaverItem[];
  inactiveMissingDateOfExit: EmployeeRow[];
  employees: EmployeeRow[];
}


function departmentsFromRow(departmentsDisplay: string): string[] {
  if (!departmentsDisplay || departmentsDisplay === '—') return [];
  return departmentsDisplay
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function WorkforceCelebrationRow({
  fullName,
  primaryDepartmentName,
  occurrenceLocalDate,
  milestoneYears,
}: {
  fullName: string;
  primaryDepartmentName?: string | null;
  occurrenceLocalDate: string;
  milestoneYears?: number | null;
}) {
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Typography variant="body2" fontWeight={600} component="div">
        {fullName}
      </Typography>
      {milestoneYears != null ? (
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mt: 0.35 }}>
          {milestoneYears} years
        </Typography>
      ) : null}
      {primaryDepartmentName ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{
            mt: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={primaryDepartmentName}
        >
          {primaryDepartmentName}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: 'block', fontWeight: 500 }}>
        {formatDate(occurrenceLocalDate)}
      </Typography>
    </Box>
  );
}

type WorkforceSortField = 'name' | 'businessUnit' | 'tenure' | 'engagement' | 'birthday' | 'nextAnniversary';

export default function OrganisationWorkforcePage() {
  const navigate = useNavigate();
  const { viewType } = useParams<{ viewType: OrganogramView }>();
  const { user } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const normalizedView: OrganogramView | null =
    viewType && ['team', 'department', 'businessunit', 'all'].includes(viewType) ? (viewType as OrganogramView) : null;
  const pageTitle = normalizedView === 'team'
    ? 'My Team'
    : normalizedView === 'department'
      ? 'My Department'
      : normalizedView === 'businessunit'
        ? 'My Business Unit'
        : 'All';
  const isAdmin = (user?.roles ?? []).includes('Administrator');
  const isSeniorManager = (user?.roles ?? []).includes('SeniorManager');
  const isManager = (user?.roles ?? []).includes('Manager');
  const canExport = isAdmin || isSeniorManager || isManager;

  const [data, setData] = useState<WorkforceInsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string>('all');
  const [profileCompletenessFilter, setProfileCompletenessFilter] = useState<
    'all' | 'missing_dob' | 'missing_engagement' | 'missing_company' | 'missing_role_template' | 'missing_exit'
  >('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [celebrationsOpen, setCelebrationsOpen] = useState(true);
  const [employeesSectionOpen, setEmployeesSectionOpen] = useState(true);
  const [sortField, setSortField] = useState<WorkforceSortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [helpExpanded, setHelpExpanded] = useState(false);
  usePageAgentContext({
    pageId: 'organisation-workforce',
    pageTitle: 'Organisation Workforce',
    viewMode: normalizedView,
    visibleSections: ['tenure', 'joiners-leavers', 'birthdays-anniversaries', 'employees-table'],
    suggestedPrompts: [
      'How should I interpret these workforce metrics?',
      'What is included in the tenure calculations?',
      'How do team and organisation scopes differ here?',
    ],
  });

  const celebrationsSectionRef = useRef<HTMLDivElement | null>(null);
  const employeesSectionRef = useRef<HTMLDivElement | null>(null);
  const tenureSectionRef = useRef<HTMLDivElement | null>(null);
  const attritionSectionRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!normalizedView) return;
    setLoading(true);
    setError('');
    try {
      const endpoint = API_ENDPOINTS.USERS.WORKFORCE_INSIGHTS(normalizedView);
      const res = await apiService.get<WorkforceInsightsResponse>(endpoint);
      setData(res.data);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('this data')));
    } finally {
      setLoading(false);
    }
  }, [normalizedView]);

  useEffect(() => {
    if (normalizedView) void load();
  }, [normalizedView, load]);

  const departmentOptions = useMemo(() => {
    if (!data?.employees?.length) return [] as string[];
    const set = new Set<string>();
    for (const e of data.employees) {
      for (const d of departmentsFromRow(e.departmentsDisplay)) {
        set.add(d);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.employees]);

  const businessUnitFilterMeta = useMemo(() => {
    if (!data?.employees?.length) return { unitNames: [] as string[], hasUnassigned: false };
    const set = new Set<string>();
    let hasUnassigned = false;
    for (const e of data.employees) {
      const n = e.businessUnitName?.trim();
      if (!n) hasUnassigned = true;
      else set.add(n);
    }
    return {
      unitNames: Array.from(set).sort((a, b) => a.localeCompare(b)),
      hasUnassigned,
    };
  }, [data?.employees]);

  const filteredEmployees = useMemo(() => {
    const source =
      profileCompletenessFilter === 'missing_exit'
        ? (data?.inactiveMissingDateOfExit ?? [])
        : (data?.employees ?? []);
    if (source.length === 0 && profileCompletenessFilter !== 'missing_exit' && !data?.employees) return [];
    const q = searchQuery.trim().toLowerCase();
    let list = source.filter((e) => {
      if (departmentFilter !== 'all' && !departmentsFromRow(e.departmentsDisplay).includes(departmentFilter)) {
        return false;
      }
      if (businessUnitFilter === 'all') return true;
      if (businessUnitFilter === '__none__') return !e.businessUnitName?.trim();
      return e.businessUnitName?.trim() === businessUnitFilter;
    });
    if (profileCompletenessFilter === 'missing_dob') {
      list = list.filter((e) => !e.dateOfBirth);
    } else if (profileCompletenessFilter === 'missing_engagement') {
      list = list.filter((e) => !e.dateOfEngagement);
    } else if (profileCompletenessFilter === 'missing_company') {
      list = list.filter((e) => e.missingCompanyForLeave);
    } else if (profileCompletenessFilter === 'missing_role_template') {
      list = list.filter((e) => e.missingRoleTemplate);
    }
    if (q) {
      list = list.filter(
        (e) =>
          e.fullName.toLowerCase().includes(q) || (e.email?.toLowerCase().includes(q) ?? false)
      );
    }
    const dir = sortDirection === 'asc' ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      if (sortField === 'name') {
        return dir * a.fullName.localeCompare(b.fullName);
      }
      if (sortField === 'businessUnit') {
        const sa = (a.businessUnitName ?? '').trim().toLowerCase();
        const sb = (b.businessUnitName ?? '').trim().toLowerCase();
        if (sa !== sb) return dir * sa.localeCompare(sb);
        return dir * a.fullName.localeCompare(b.fullName);
      }
      if (sortField === 'tenure') {
        const ta = a.tenureYearsApprox ?? -1;
        const tb = b.tenureYearsApprox ?? -1;
        if (ta !== tb) return dir * (ta - tb);
        return a.fullName.localeCompare(b.fullName);
      }
      if (sortField === 'engagement') {
        const da = a.dateOfEngagement ? parseLocalDate(a.dateOfEngagement).getTime() : 0;
        const db = b.dateOfEngagement ? parseLocalDate(b.dateOfEngagement).getTime() : 0;
        if (da !== db) return dir * (da - db);
        return a.fullName.localeCompare(b.fullName);
      }
      if (sortField === 'birthday') {
        const ba = utcMs(a.dateOfBirth);
        const bb = utcMs(b.dateOfBirth);
        if (ba !== bb) return dir * (ba - bb);
        return a.fullName.localeCompare(b.fullName);
      }
      if (sortField === 'nextAnniversary') {
        const na = a.nextWorkAnniversaryLocal ? utcMs(a.nextWorkAnniversaryLocal) : null;
        const nb = b.nextWorkAnniversaryLocal ? utcMs(b.nextWorkAnniversaryLocal) : null;
        const va =
          na ?? (sortDirection === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
        const vb =
          nb ?? (sortDirection === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
        if (va !== vb) return dir * (va - vb);
        return a.fullName.localeCompare(b.fullName);
      }
      return a.fullName.localeCompare(b.fullName);
    });
    return sorted;
  }, [data?.employees, data?.inactiveMissingDateOfExit, departmentFilter, businessUnitFilter, profileCompletenessFilter, searchQuery, sortField, sortDirection]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEmployees.slice(start, start + rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  const tenureChartData = useMemo(() => {
    if (!data?.tenureDistribution) return [];
    const d = data.tenureDistribution;
    return [
      { label: '< 1 year', count: d.underOneYear },
      { label: '1–3 years', count: d.oneToThreeYears },
      { label: '3–5 years', count: d.threeToFiveYears },
      { label: '5–10 years', count: d.fiveToTenYears },
      { label: '10+ years', count: d.tenYearsPlus },
    ];
  }, [data?.tenureDistribution]);

  const attritionChartData = useMemo(() => {
    if (!data?.calendarYearAttrition?.length) return [];
    return data.calendarYearAttrition.map((row) => ({
      label: row.isYearToDate ? `${row.year} (YTD)` : String(row.year),
      rate: row.attritionRatePercent ?? 0,
      leavers: row.leavers,
    }));
  }, [data?.calendarYearAttrition]);

  const currentYearAttrition = useMemo(
    () => data?.calendarYearAttrition?.find((r) => r.isYearToDate) ?? null,
    [data?.calendarYearAttrition],
  );

  const handleSort = (field: WorkforceSortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getWorkforceSortIcon = (field: WorkforceSortField) =>
    sortField !== field ? (
      <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />
    ) : sortDirection === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
    );

  const WorkforceSortableHeader = ({ field, label }: { field: WorkforceSortField; label: string }) => (
    <Box
      component="span"
      onClick={() => handleSort(field)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { opacity: 0.7 },
      }}
    >
      <strong>{label}</strong>
      {getWorkforceSortIcon(field)}
    </Box>
  );

  const handleExportExcel = () => {
    downloadWorkforceInsightsExcel(filteredEmployees);
  };

  const scrollSectionIntoView = useCallback((section: HTMLDivElement | null) => {
    if (!section) return;
    const headerOffset = 110;
    const elementTop = section.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(elementTop - headerOffset, 0);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  }, []);

  const handleHeadcountCardClick = useCallback(() => {
    setEmployeesSectionOpen(true);
    requestAnimationFrame(() => {
      scrollSectionIntoView(employeesSectionRef.current);
    });
  }, [scrollSectionIntoView]);

  const focusEmployeesSection = useCallback(() => {
    setEmployeesSectionOpen(true);
    requestAnimationFrame(() => {
      scrollSectionIntoView(employeesSectionRef.current);
    });
  }, [scrollSectionIntoView]);

  const handleMissingDobCardClick = useCallback(() => {
    setProfileCompletenessFilter('missing_dob');
    setPage(0);
    focusEmployeesSection();
  }, [focusEmployeesSection]);

  const handleMissingEngagementCardClick = useCallback(() => {
    setProfileCompletenessFilter('missing_engagement');
    setPage(0);
    focusEmployeesSection();
  }, [focusEmployeesSection]);

  const handleMissingCompanyCardClick = useCallback(() => {
    setProfileCompletenessFilter('missing_company');
    setPage(0);
    focusEmployeesSection();
  }, [focusEmployeesSection]);

  const handleMissingRoleTemplateCardClick = useCallback(() => {
    setProfileCompletenessFilter('missing_role_template');
    setPage(0);
    focusEmployeesSection();
  }, [focusEmployeesSection]);

  const handleMissingExitCardClick = useCallback(() => {
    setProfileCompletenessFilter('missing_exit');
    setPage(0);
    focusEmployeesSection();
  }, [focusEmployeesSection]);

  const handleThisPeriodCardClick = useCallback(() => {
    setCelebrationsOpen(true);
    requestAnimationFrame(() => {
      scrollSectionIntoView(celebrationsSectionRef.current);
    });
  }, [scrollSectionIntoView]);

  const handleTenureCardClick = useCallback(() => {
    requestAnimationFrame(() => {
      scrollSectionIntoView(tenureSectionRef.current);
    });
  }, [scrollSectionIntoView]);

  const handleAttritionCardClick = useCallback(() => {
    requestAnimationFrame(() => {
      scrollSectionIntoView(attritionSectionRef.current);
    });
  }, [scrollSectionIntoView]);

  if (!normalizedView) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', py: 4 }}>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Invalid workforce view. Please select Team, My Department, My Business Unit, or All.
            </Alert>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/workforce/all')}
            >
              Go to Workforce
            </Button>
          </Card>
        </Box>
      </AppLayout>
    );
  }

  if (loading && !data) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} title="Back" size="small" sx={{ mr: -0.5, mt: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <GroupsIcon sx={{ fontSize: 32, color: 'primary.main', mt: 0.5 }} />
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
                {pageTitle}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {data && (
          <>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  Overview
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: data.companyHeadcount != null ? 3 : 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_PURPLE}
                    label="Headcount"
                    value={data.headcount}
                    icon={<GroupsIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleHeadcountCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: data.companyHeadcount != null ? 3 : 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_PURPLE}
                    label="Median tenure (years)"
                    value={data.medianYearsTenure != null ? data.medianYearsTenure.toFixed(1) : '—'}
                    icon={<TrendingUpIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleTenureCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: data.companyHeadcount != null ? 3 : 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_PURPLE}
                    label="Mean tenure (years)"
                    value={data.meanYearsTenure != null ? data.meanYearsTenure.toFixed(2) : '—'}
                    icon={<AnalyticsIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleTenureCardClick}
                  />
                </Grid>
                {data.companyHeadcount != null ? (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <WorkforceGradientStatCard
                      sx={STAT_CARD_PURPLE}
                      label="Company mean tenure (years)"
                      value={data.companyMeanYearsTenure != null ? data.companyMeanYearsTenure.toFixed(2) : '—'}
                      icon={<TrendingUpIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                      onClick={handleTenureCardClick}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CelebrationIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  This period
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_GREEN}
                    label="Birthdays this month"
                    value={data.birthdaysThisMonthCount}
                    icon={<CakeOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleThisPeriodCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_GREEN}
                    label="Work anniversaries this month"
                    value={data.workAnniversariesThisMonthCount}
                    icon={<EventAvailableIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleThisPeriodCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_GREEN}
                    label="New joiners (90 days)"
                    value={data.newJoinersLast90DaysCount}
                    icon={<PersonAddAltIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleThisPeriodCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_GREEN}
                    label="Milestone anniversaries (this quarter)"
                    value={data.milestoneAnniversariesThisQuarterCount}
                    icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleThisPeriodCardClick}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }} ref={attritionSectionRef}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonRemoveOutlinedIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  Attrition
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label={currentYearAttrition ? `Attrition ${currentYearAttrition.year} (YTD)` : 'Attrition (YTD)'}
                    value={
                      currentYearAttrition?.attritionRatePercent != null
                        ? `${currentYearAttrition.attritionRatePercent.toFixed(1)}%`
                        : '—'
                    }
                    icon={<PersonRemoveOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleAttritionCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Leavers this year"
                    value={currentYearAttrition?.leavers ?? data.leaversThisCalendarYear?.length ?? 0}
                    icon={<PersonRemoveOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleAttritionCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Avg headcount (YTD)"
                    value={
                      currentYearAttrition != null
                        ? currentYearAttrition.averageHeadcount.toFixed(0)
                        : '—'
                    }
                    icon={<GroupsIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleAttritionCardClick}
                  />
                </Grid>
              </Grid>
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(145deg, rgba(255, 167, 38, 0.08) 0%, rgba(251, 140, 0, 0.05) 100%)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight="700" color="primary" sx={{ mb: 2 }}>
                    Calendar-year attrition (last 5 years)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Leavers ÷ average headcount for each calendar year. People without a date of engagement are excluded.
                    The current year is year-to-date.
                  </Typography>
                  {attritionChartData.length > 0 ? (
                    <Box sx={{ width: '100%', height: 260, mb: 2 }}>
                      <ResponsiveContainer>
                        <BarChart data={attritionChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals unit="%" />
                          <Tooltip
                            formatter={(value, name) =>
                              name === 'rate' ? [`${Number(value).toFixed(1)}%`, 'Attrition'] : [value, 'Leavers']
                            }
                          />
                          <Bar dataKey="rate" fill="#fb8c00" name="rate" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : null}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Year</TableCell>
                          <TableCell align="right">Leavers</TableCell>
                          <TableCell align="right">Opening</TableCell>
                          <TableCell align="right">Closing</TableCell>
                          <TableCell align="right">Average</TableCell>
                          <TableCell align="right">Attrition %</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.calendarYearAttrition ?? []).map((row) => (
                          <TableRow key={row.year}>
                            <TableCell>
                              {row.year}
                              {row.isYearToDate ? ' (YTD)' : ''}
                            </TableCell>
                            <TableCell align="right">{row.leavers}</TableCell>
                            <TableCell align="right">{row.openingHeadcount}</TableCell>
                            <TableCell align="right">{row.closingHeadcount}</TableCell>
                            <TableCell align="right">{row.averageHeadcount.toFixed(1)}</TableCell>
                            <TableCell align="right">
                              {row.attritionRatePercent != null ? `${row.attritionRatePercent.toFixed(1)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
              {(data.leaversThisCalendarYear?.length ?? 0) > 0 ? (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                      Leavers this calendar year
                    </Typography>
                    <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                      {data.leaversThisCalendarYear.map((leaver) => (
                        <Box
                          key={leaver.userId}
                          component="li"
                          sx={{
                            listStyle: 'none',
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none', pb: 0 },
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {leaver.fullName}
                          </Typography>
                          {leaver.primaryDepartmentName ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                              {leaver.primaryDepartmentName}
                            </Typography>
                          ) : null}
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                            Left {formatDate(leaver.dateOfExit)}
                            {leaver.tenureAtExitDisplay ? ` · Tenure at exit: ${leaver.tenureAtExitDisplay}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FactCheckOutlinedIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  Profile completeness
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Missing date of birth"
                    value={data.missingDateOfBirthCount}
                    icon={<WarningAmberOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleMissingDobCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Missing engagement date"
                    value={data.missingDateOfEngagementCount}
                    icon={<WarningAmberOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleMissingEngagementCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Missing date of exit"
                    value={data.missingDateOfExitCount}
                    icon={<PersonRemoveOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleMissingExitCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Missing company for leave"
                    value={data.missingCompanyForLeaveCount}
                    icon={<BusinessOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleMissingCompanyCardClick}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <WorkforceGradientStatCard
                    sx={STAT_CARD_ORANGE}
                    label="Missing role template"
                    value={data.missingRoleTemplateCount}
                    icon={<AssignmentOutlinedIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.9)' }} />}
                    onClick={handleMissingRoleTemplateCardClick}
                  />
                </Grid>
              </Grid>
            </Box>

            <Card
              elevation={0}
              sx={{
                mb: 3,
                background: 'linear-gradient(145deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.05) 100%)',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              }}
              ref={tenureSectionRef}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUpIcon color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Tenure distribution
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={tenureChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#667eea" name="People" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }} ref={celebrationsSectionRef}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { opacity: 0.85 },
                  }}
                  onClick={() => setCelebrationsOpen((o) => !o)}
                >
                  <Typography variant="subtitle1">This month and this quarter</Typography>
                  <IconButton size="small" aria-label={celebrationsOpen ? 'Collapse' : 'Expand'}>
                    {celebrationsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={celebrationsOpen}>
                  <Grid container spacing={0} sx={{ mt: 1 }}>
                    <Grid
                      size={{ xs: 12, md: 3 }}
                      sx={{
                        pr: { xs: 0, md: 2.5 },
                        pb: { xs: 3, md: 0 },
                        borderRight: { md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.08, display: 'block', mb: 1.5 }}>
                        Birthdays this month
                      </Typography>
                      {data.birthdaysThisMonth.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No birthdays this month.
                        </Typography>
                      ) : (
                        <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                          {data.birthdaysThisMonth.map((b) => (
                            <WorkforceCelebrationRow
                              key={b.userId}
                              fullName={b.fullName}
                              primaryDepartmentName={b.primaryDepartmentName}
                              occurrenceLocalDate={b.occurrenceLocalDate}
                            />
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 3 }}
                      sx={{
                        px: { xs: 0, md: 2.5 },
                        py: { xs: 3, md: 0 },
                        borderRight: { md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.08, display: 'block', mb: 1.5 }}>
                        Work anniversaries this month
                      </Typography>
                      {data.workAnniversariesThisMonth.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No work anniversaries this month.
                        </Typography>
                      ) : (
                        <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                          {data.workAnniversariesThisMonth.map((b) => (
                            <WorkforceCelebrationRow
                              key={b.userId}
                              fullName={b.fullName}
                              primaryDepartmentName={b.primaryDepartmentName}
                              occurrenceLocalDate={b.occurrenceLocalDate}
                            />
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 3 }}
                      sx={{
                        px: { xs: 0, md: 2.5 },
                        py: { xs: 3, md: 0 },
                        borderRight: { md: '1px solid' },
                        borderBottom: { xs: '1px solid', md: 'none' },
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.08, display: 'block', mb: 1.5 }}>
                        New joiners (90 days)
                      </Typography>
                      {data.newJoinersLast90Days.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No new joiners in the last 90 days.
                        </Typography>
                      ) : (
                        <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                          {data.newJoinersLast90Days.map((b) => (
                            <WorkforceCelebrationRow
                              key={`joiner-${b.userId}`}
                              fullName={b.fullName}
                              primaryDepartmentName={b.primaryDepartmentName}
                              occurrenceLocalDate={b.occurrenceLocalDate}
                            />
                          ))}
                        </Stack>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} sx={{ pl: { xs: 0, md: 2.5 }, pt: { xs: 3, md: 0 } }}>
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.08, display: 'block', mb: 1.5 }}>
                        Milestone anniversaries (this quarter)
                      </Typography>
                      {data.milestoneAnniversariesThisQuarter.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No milestone anniversaries in this quarter.
                        </Typography>
                      ) : (
                        <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                          {data.milestoneAnniversariesThisQuarter.map((b) => (
                            <WorkforceCelebrationRow
                              key={`${b.userId}-${b.milestoneYears}`}
                              fullName={b.fullName}
                              primaryDepartmentName={b.primaryDepartmentName}
                              occurrenceLocalDate={b.occurrenceLocalDate}
                              milestoneYears={b.milestoneYears ?? undefined}
                            />
                          ))}
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                </Collapse>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mb: 3 }} ref={employeesSectionRef}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => setEmployeesSectionOpen((o) => !o)}
                >
                  <Typography variant="subtitle1">Employees</Typography>
                  <IconButton size="small" aria-label={employeesSectionOpen ? 'Collapse' : 'Expand'}>
                    {employeesSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={employeesSectionOpen}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                    Use the dropdowns for department and business unit. Search matches name or email only. Export to Excel
                    includes every row that matches your current filters and sort order.
                  </Typography>
                  {profileCompletenessFilter !== 'all' ? (
                    <Alert
                      severity="info"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => {
                            setProfileCompletenessFilter('all');
                            setPage(0);
                          }}
                        >
                          Clear
                        </Button>
                      }
                    >
                      Showing only employees with{' '}
                      {profileCompletenessFilter === 'missing_dob'
                        ? 'missing date of birth'
                        : profileCompletenessFilter === 'missing_engagement'
                          ? 'missing engagement date'
                          : profileCompletenessFilter === 'missing_exit'
                            ? 'no date of exit (inactive accounts — set exit date in User Management)'
                            : profileCompletenessFilter === 'missing_company'
                              ? 'missing company or valid country for leave'
                              : 'missing role template (reviews will not include objectives or KPIs)'}
                      .
                    </Alert>
                  ) : null}
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 220 }} size="small">
                      <InputLabel id="dept-filter-label">Department</InputLabel>
                      <Select
                        labelId="dept-filter-label"
                        label="Department"
                        value={departmentFilter}
                        onChange={(e) => {
                          setDepartmentFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="all">All departments</MenuItem>
                        {departmentOptions.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 220 }} size="small">
                      <InputLabel id="bu-filter-label">Business unit</InputLabel>
                      <Select
                        labelId="bu-filter-label"
                        label="Business unit"
                        value={businessUnitFilter}
                        onChange={(e) => {
                          setBusinessUnitFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="all">All business units</MenuItem>
                        {businessUnitFilterMeta.hasUnassigned ? (
                          <MenuItem value="__none__">No business unit</MenuItem>
                        ) : null}
                        {businessUnitFilterMeta.unitNames.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {canExport ? (
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        disabled={filteredEmployees.length === 0}
                      >
                        Export to Excel
                      </Button>
                    ) : null}
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="name" label="Name" />
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <strong>Department(s)</strong>
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="businessUnit" label="Business unit" />
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="birthday" label="Date of birth" />
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="engagement" label="Date of engagement" />
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="tenure" label="Length of service" />
                          </TableCell>
                          <TableCell sx={WORKFORCE_TABLE_HEAD_CELL_SX}>
                            <WorkforceSortableHeader field="nextAnniversary" label="Next anniversary" />
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEmployees.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none' }}>
                              <Typography color="text.secondary">
                                {data.employees.length === 0
                                  ? 'No employees to show.'
                                  : 'No matches for your search or filters.'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedRows.map((row) => (
                            <TableRow key={row.userId} hover>
                              <TableCell
                                sx={{ maxWidth: 280, verticalAlign: 'top' }}
                                title={
                                  row.email?.trim()
                                    ? `${row.fullName} · ${row.email}`
                                    : row.fullName
                                }
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block',
                                  }}
                                >
                                  {row.fullName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {row.email ?? '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={WORKFORCE_BODY_ELLIPSIS_CELL_SX} title={row.departmentsDisplay}>
                                {row.departmentsDisplay}
                              </TableCell>
                              <TableCell sx={WORKFORCE_BODY_ELLIPSIS_CELL_SX} title={row.businessUnitName ?? undefined}>
                                {row.businessUnitName ?? '—'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {row.dateOfBirth ? formatDate(row.dateOfBirth) : '—'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {row.dateOfEngagement ? formatDate(row.dateOfEngagement) : '—'}
                              </TableCell>
                              <TableCell
                                sx={[WORKFORCE_BODY_ELLIPSIS_CELL_SX, { maxWidth: 200 }]}
                                title={row.tenureDisplay ?? undefined}
                              >
                                {row.tenureDisplay ?? '—'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {row.nextWorkAnniversaryLocal ? formatDate(row.nextWorkAnniversaryLocal) : '—'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredEmployees.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                  />
                </Collapse>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
      {showLegacyHelp && (
        <OrganisationWorkforceHelpSidebar
          isExpanded={helpExpanded}
          onToggle={() => setHelpExpanded((v) => !v)}
          scope={normalizedView === 'team' ? 'team' : 'organisation'}
        />
      )}
    </AppLayout>
  );
}
